//#region IMPORTS
import { Listbox } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { Fragment } from "react";
import { statusColorByName } from "../../helpers/statusColors";
import { useAppSelector } from "../../store/configureStore";
import { ORDERSTATUS } from "../../models/order";
import isShopUser from "../../helpers/isShopUser";
import classNames from "../../helpers/classNames";
//#endregion

//#region INTERFACE
interface Props {
  currentStatus: string;
  // When editable (and options + handler are provided) a chevron dropdown is
  // shown; otherwise the pill renders read-only (the former badge).
  editable?: boolean;
  orderStatusses?: string[];
  changeOrderStatusHandler?: (status: string) => void;
  // When provided, clicking the pill body (not the chevron) filters the list by
  // this status. Independent of `editable` — the chevron dropdown still changes
  // the status when present.
  onStatusClick?: (status: string) => void;
  disabled?: boolean;
}
//#endregion

export default function OrderStatusControl({
  currentStatus,
  editable = false,
  orderStatusses = [],
  changeOrderStatusHandler,
  onStatusClick,
  disabled = false,
}: Props) {
  //#region SETUP
  // Shop users may not see the word "Archived"; show it (and colour it) as
  // "Delivered" for them. Cosmetic only — the underlying status/filter value
  // stays the real one.
  const user = useAppSelector((state) => state.account.user);
  const displayStatus =
    isShopUser(user) && currentStatus === ORDERSTATUS.archived
      ? ORDERSTATUS.delivered
      : currentStatus;

  const colors = statusColorByName(disabled ? "-" : displayStatus);
  const showDropdown =
    editable && !disabled && orderStatusses.length > 0 && !!changeOrderStatusHandler;
  const clickable = !!onStatusClick && !disabled;
  //#endregion

  //#region RENDER
  const Dot = (
    <svg
      viewBox="0 0 6 6"
      aria-hidden="true"
      className={classNames(colors.fill, "h-1.5 w-1.5")}
    >
      <circle r={3} cx={3} cy={3} />
    </svg>
  );

  return (
    <div className={classNames(disabled ? "opacity-30" : "opacity-100", "flex items-center")}>
      <Listbox
        value={currentStatus}
        disabled={!showDropdown}
        onChange={(status) => changeOrderStatusHandler?.(status)}
      >
        <div className="relative">
          <div className="inline-flex divide-x divide-white rounded-full outline-none">
            {clickable ? (
              <button
                type="button"
                onClick={() => onStatusClick?.(currentStatus)}
                title={`Filter by ${displayStatus}`}
                className={classNames(
                  colors.bg,
                  colors.text,
                  showDropdown ? "rounded-l-full" : "rounded-full",
                  "inline-flex items-center gap-x-1 px-2 py-0.5 outline-none hover:opacity-70",
                )}
              >
                {Dot}
                <span className="whitespace-nowrap text-[10px] font-semibold">
                  {displayStatus}
                </span>
              </button>
            ) : (
              <div
                className={classNames(
                  colors.bg,
                  colors.text,
                  showDropdown ? "rounded-l-full" : "rounded-full",
                  "inline-flex items-center gap-x-1 px-2 py-0.5",
                )}
              >
                {Dot}
                <span className="whitespace-nowrap text-[10px] font-semibold">
                  {displayStatus}
                </span>
              </div>
            )}

            {showDropdown && (
              <Listbox.Button
                className={classNames(
                  colors.bg,
                  colors.text,
                  "inline-flex items-center rounded-l-none rounded-r-full p-0.5 outline-none hover:opacity-50",
                )}
              >
                <ChevronDownIcon className={classNames(colors.text, "h-3.5 w-3.5")} />
              </Listbox.Button>
            )}
          </div>

          {showDropdown && (
            <Listbox.Options className="absolute right-0 z-10 mt-2 max-h-52 w-48 origin-top-right divide-y divide-gray-200 overflow-y-auto rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none">
              {orderStatusses.map((status) => {
                const optColors = statusColorByName(status);
                return (
                  <Listbox.Option key={status} value={status} as={Fragment}>
                    {() => (
                      <li
                        className={classNames(
                          optColors.text,
                          "group cursor-pointer select-none px-4 py-2 text-xs hover:opacity-70",
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <svg
                            viewBox="0 0 6 6"
                            aria-hidden="true"
                            className={classNames(optColors.fill, "h-1.5 w-1.5")}
                          >
                            <circle r={3} cx={3} cy={3} />
                          </svg>
                          <span className="font-semibold">{status}</span>
                        </div>
                      </li>
                    )}
                  </Listbox.Option>
                );
              })}
            </Listbox.Options>
          )}
        </div>
      </Listbox>
    </div>
  );
  //#endregion
}
